import React from 'react'
import PropTypes from 'prop-types'
import Modal from '../shared/Modal'
import Loader from '../shared/Loader'
import ExcelOptions from './components/ExcelOptions'
import PDFOptions from './components/PDFOptions'
import PhotosOptions from './components/PhotosOptions'
import InitialView from './components/InitialView'
import ModalFooter from './components/ModalFooter'
import { useExportModal, VIEWS } from './hooks/useExportModal'

const ExportModal = ({
  collect,
  draft,
  sections,
  exportUrl,
  pdfExportUrl,
  photosExportUrl,
  buttonLabel,
  buttonClassName,
  iconClassName,
  defaultView,
  allowedExports,
  currentSectionId,
  ignoreFilters
}) => {
  const {
    isOpen,
    currentView,
    currentStep,
    loading,
    formData,
    handleClose,
    handleOpen,
    handleExcelClick,
    handlePDFClick,
    handlePhotosClick,
    handlePhotosCustomize,
    handleBack,
    handleExcelExport,
    handlePhotosExport,
    handlePDFExport,
    handleSectionChange,
    handleVariablesChange,
    handleExportAsZipChange
  } = useExportModal({
    collect,
    draft,
    exportUrl,
    pdfExportUrl,
    photosExportUrl,
    defaultView,
    currentSectionId,
    ignoreFilters
  })

  const renderContent = () => {
    if (loading)
      return <Loader text="Veuillez patienter..." size="small" />

    switch (currentView) {
    case VIEWS.INITIAL:
      return (
        <InitialView
          allowedExports={allowedExports}
          onExcelClick={handleExcelClick}
          onPDFClick={handlePDFClick}
          onPhotosClick={handlePhotosClick}
        />
      )
    case VIEWS.EXCEL_OPTIONS:
      return (
        <ExcelOptions
          collect={collect}
          draft={draft}
          onExport={handleExcelExport}
        />
      )
    case VIEWS.PDF_OPTIONS:
      return (
        <PDFOptions
          sections={sections}
          value={formData.sections}
          withVariables={formData.allVariables}
          exportAsZip={formData.exportAsZip}
          onChange={handleSectionChange}
          onVariablesChange={handleVariablesChange}
          onExportAsZipChange={handleExportAsZipChange}
        />
      )
    case VIEWS.PHOTOS_OPTIONS:
      return (
        <PhotosOptions
          currentSectionId={currentSectionId}
          onCustomize={handlePhotosCustomize}
          onExportAll={handlePhotosExport}
        />
      )
    default:
      return null
    }
  }
  const modalTitle = (
    <>
      <i className="fas fa-share-square" />
      <span className="ml-2">
        Exporter les ressources
      </span>
    </>
  )

  return (
    <>
      <button
        type="button"
        className={buttonClassName}
        onClick={handleOpen}
      >
        {iconClassName ? (
          <>
            <i className={iconClassName} />
            {' '}
            {buttonLabel}
          </>
        ) : null}
      </button>
      <Modal
        isOpen={isOpen}
        title={modalTitle}
        footer={
          <ModalFooter
            currentView={currentView}
            currentStep={currentStep}
            loading={loading}
            onBack={handleBack}
            onExcelExport={handleExcelExport}
            onPDFExport={handlePDFExport}
            onPhotosExport={handlePhotosExport}
            onClose={handleClose}
          />
        }
        onClose={handleClose}
      >
        {renderContent()}
      </Modal>
    </>
  )
}

ExportModal.defaultProps = {
  buttonClassName: 'btn btn-primary',
  buttonLabel: 'Exporter',
  currentSectionId: null,
  defaultView: VIEWS.INITIAL,
  draft: null,
  iconClassName: 'fas fa-file-export',
  ignoreFilters: false,
  pdfExportUrl: null,
  photosExportUrl: null,
  sections: []
}

ExportModal.propTypes = {
  allowedExports: PropTypes.arrayOf(PropTypes.string).isRequired,
  buttonClassName: PropTypes.string,
  buttonLabel: PropTypes.string,
  collect: PropTypes.shape({
    id: PropTypes.number.isRequired
  }).isRequired,
  currentSectionId: PropTypes.number,
  defaultView: PropTypes.string,
  draft: PropTypes.shape({
    id: PropTypes.number.isRequired
  }),
  exportUrl: PropTypes.string.isRequired,
  iconClassName: PropTypes.string,
  ignoreFilters: PropTypes.bool,
  pdfExportUrl: PropTypes.string,
  photosExportUrl: PropTypes.string,
  sections: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  }))
}

export default ExportModal
